import { FetchSliderPage, FetchSliderPageParams } from '@dynamic/ui';

import { axios, FeedResponse } from '#/client';
import { changeUrlParams } from '#/service/utils';
import { historyActions } from '#/store/history';
import { translationStore } from '#/store/translation/translation.store';

let aborter = new AbortController();

export const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_MULTIVIEW_PAGE_SIZE = 100;

const DONT_CACHE = [
  '/api/core/user/watch-history',
  '/api/core/user/favorites',
  '/api/core/recommendation/user',
];

const cache: { [key: string]: FeedResponse } = {};

/**
 * Fetch a generic feed by passing the Core API URL coming from DynUi to the server to call it. Supports in-memory caching
 * @param feed {string} Core API feed URL specified in the `feed` DynUi property on the component.
 * @param page {number} Page number. 1-based.
 * @param pageSize {number} Page size.
 * @returns {Promise<FeedResponse>} Promise of the response.
 */
export const fetchFeed = async (
  feed: string,
  page: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<FeedResponse> => {
  const locale = translationStore.locale;
  const key = `${feed}:${page}:${pageSize}:${locale}`;
  const cachedResponse = cache[key];

  if (cachedResponse) {
    // we have to do this every time, even if the FeedResponse itself is cached, because the history info might have changed.
    return Promise.resolve({
      ...cachedResponse,
      data: historyActions.cloneWithWatchedObjects(cachedResponse.data),
    });
  }

  // `feed` contains the initial page and pageSize params that we have to overwrite with the current arguments.
  const pageFeed = changeUrlParams(feed, { page, pageSize });

  return axios
    .get<FeedResponse>(pageFeed, { signal: aborter.signal })
    .then((res) => {
      const feedResponse = res?.data;

      if (!feedResponse) {
        throw new Error('Empty response.');
      }

      const isCacheable = !DONT_CACHE.find((pattern) => feed.includes(pattern));

      if (isCacheable) {
        cache[key] = feedResponse;
      }

      return {
        ...feedResponse,
        data: historyActions.cloneWithWatchedObjects(feedResponse.data),
      };
    });
};

/**
 * Create the fetcher function to be passed to DynUi via `setFetchSliderPage`.
 * The "slider" in the name is historic and outdated, since the same function is used for all DynUi fetches.
 * - Slider: initial page is prefetched by client, subsequent pages fetched here.
 * - Grid: initial page is prefetched by client, subsequent pages fetched here.
 * - MultiView: full content is fetched here.
 * @returns {FetchSliderPage} Fetcher function for DynUi.
 */
export const makeFetcher =
  (): FetchSliderPage =>
  async ({
    feed,
    opts: { start, size },
    componentType,
  }: FetchSliderPageParams) => {
    if (componentType === 'MULTI_VIEW') {
      size = DEFAULT_MULTIVIEW_PAGE_SIZE;
    }
    const page = Math.round(start / size + 1);

    return fetchFeed(feed, page, size).catch((err: Error) => {
      console.error(`Container fetch failed`, {
        feed,
        error: err.message,
      });
      throw err;
    });
  };

/** This method cancels active API requests for dynamic UI stuff */
export function cancelOldRequests() {
  // Sending abort to each requests which listens to the `aborter.signal`
  aborter.abort();

  // AbortController needs to be recreated once aborted.
  aborter = new AbortController();
}
